import { G } from '@mobily/ts-belt';
import { FC } from 'react';
import {
  BooleanField,
  Identifier,
  RaRecord,
  ReferenceManyField,
  SelectField,
  TextField,
  useRecordContext,
  useResourceContext,
} from 'react-admin';

import {
  B2bEmployeeDocumentDetails,
  B2bEmployeeDocumentsListItem,
  B2bEmployeesDocumentsTypeCommon,
  getB2bEmployeeDocumentsSortColumns,
} from '@Common/types/bo/b2b/employees';
import { UserDocumentSource } from '@Common/types/bo/users_documents';
import { GetBoExternalEmployeeDocumentDetailsEndpoint } from '@Common/types/bo_external/employees';

import {
  DatagridTyped,
  EmptyScreen,
  Pagination,
  ShowFields,
  ShowSection,
  ShowWithNavigation,
} from '@sh4res/bo-common/components';
import { IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { DocumentListViewerLink } from '@sh4res/bo-common/fields/DocumentListViewerLink';
import { EmptyFolderSvg } from '@sh4res/bo-common/graphics';
import { ResourceBasePath, useResourceBasePath } from '@sh4res/bo-common/hooks';
import { ColumnList, makeChoicesCommon } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const b2bEmployeeDocumentsTypesChoices = makeChoicesCommon<B2bEmployeesDocumentsTypeCommon>()({
  account_statement: 'resources.bo_common.b2b.employees.documents.type.account_statement',
  trade_confirmation: 'resources.bo_common.b2b.employees.documents.type.trade_confirmation',
  early_withdrawal_request:
    'resources.bo_common.b2b.employees.documents.type.early_withdrawal_request',
  w8ben: 'resources.bo_common.b2b.employees.documents.type.w8ben',
  annual_statement_csv: 'resources.bo_common.b2b.employees.documents.type.annual_statement_csv',
  bank_account_statement: 'resources.bo_common.b2b.employees.documents.type.bank_account_statement',
  proof_of_address: 'resources.bo_common.b2b.employees.documents.type.proof_of_address',
  account_statement_equities:
    'resources.bo_common.b2b.employees.documents.type.account_statement_equities',
  rib: 'resources.bo_common.b2b.employees.documents.type.rib',
  funding_source: 'resources.bo_common.b2b.employees.documents.type.funding_source',
  employee_savings_sepa_mandate_form_unsigned:
    'resources.bo_common.b2b.employees.documents.type.employee_savings_sepa_mandate_form_unsigned',
  employee_savings_sepa_mandate_form_signed:
    'resources.bo_common.b2b.employees.documents.type.employee_savings_sepa_mandate_form_signed',
  proof_of_identity: 'resources.bo_common.b2b.employees.documents.type.proof_of_identity',
  axa_individual_transfer_bulletin:
    'resources.bo_common.b2b.employees.documents.type.axa_individual_transfer_bulletin',
  axa_taiga_individual_transfer_form:
    'resources.bo_common.b2b.employees.documents.type.axa_taiga_individual_transfer_form',
  axa_voluntary_payment_sof_certificate:
    'resources.bo_common.b2b.employees.documents.type.axa_voluntary_payment_sof_certificate',
  b2b_account_statement_on_demand:
    'resources.bo_common.b2b.employees.documents.type.b2b_account_statement_on_demand',
  b2b_transfer_in_statement:
    'resources.bo_common.b2b.employees.documents.type.b2b_transfer_in_statement',
  b2b_employee_welcome_letter:
    'resources.bo_common.b2b.employees.documents.type.b2b_employee_welcome_letter',
  b2b_ca_shareholder_letter:
    'resources.bo_common.b2b.employees.documents.type.b2b_ca_shareholder_letter',
  b2b_ca_dividend_statement:
    'resources.bo_common.b2b.employees.documents.type.b2b_ca_dividend_statement',
  b2b_campaign_action_required_letter:
    'resources.bo_common.b2b.employees.documents.type.b2b_campaign_action_required_letter',
  b2b_campaign_confirmation:
    'resources.bo_common.b2b.employees.documents.type.b2b_campaign_confirmation',
  b2b_shareplan_operation_statement:
    'resources.bo_common.b2b.employees.documents.type.b2b_shareplan_operation_statement',
});

const ALL_DOCUMENTS_SOURCE_CHOICES = makeChoicesCommon<UserDocumentSource>()({
  bo: 'resources.bo_common.b2b.employees.documents.sources.bo',
  app: 'resources.bo_common.b2b.employees.documents.sources.app',
  alpaca: 'resources.bo_common.b2b.employees.documents.sources.alpaca',
  fourthline: 'resources.bo_common.b2b.employees.documents.sources.fourthline',
  wm_app: 'resources.bo_common.b2b.employees.documents.sources.wm_app',
});

export const b2bEmployeesDocumentsListColumns = (resourceBasePath: ResourceBasePath) =>
  ({
    id: (
      <IdField
        reference={`${resourceBasePath}employees/documents`}
        label="resources.bo_common.b2b.employees.documents.fields.id"
        data-testid="BoExternalB2bEmployeeDocuments_IdField"
      />
    ),
    employee_id: (
      <IdField
        reference={`${resourceBasePath}employees`}
        label="resources.bo_common.b2b.employees.documents.fields.employee_id"
        data-testid="BoExternalB2bEmployeeDocuments_EmployeeIdField"
      />
    ),
    document_type: (
      <SelectChipField
        choices={b2bEmployeeDocumentsTypesChoices}
        label="resources.bo_common.b2b.employees.documents.fields.document_type"
        data-testid="BoExternalB2bEmployeeDocuments_DocumentTypeField"
      />
    ),
    document_url: (
      <DocumentListViewerLink<B2bEmployeeDocumentsListItem>
        urlSource="document_url"
        nameSource="document_name"
        label="resources.bo_common.b2b.employees.documents.fields.document_url"
      />
    ),
    document_name: (
      <TextField
        label="resources.bo_common.b2b.employees.documents.fields.document_name"
        data-testid="BoExternalB2bEmployeeDocuments_DocumentNameField"
      />
    ),
    source: (
      <SelectField
        choices={ALL_DOCUMENTS_SOURCE_CHOICES}
        label="resources.bo_common.b2b.employees.documents.fields.source"
        data-testid="BoExternalB2bEmployeeDocuments_SourceField"
      />
    ),
    is_archived: (
      <BooleanField
        label="resources.bo_common.b2b.employees.documents.fields.is_archived"
        data-testid="BoExternalB2bEmployeeDocuments_IsArchivedField"
      />
    ),
    task_id: (
      <BooleanField
        label="resources.bo_common.b2b.employees.documents.fields.backoffice_task_id"
        data-testid="BoExternalB2bEmployeeDocuments_TaskIdField"
      />
    ),
    created_at: (
      <UTCDateField
        showTime={!isBoExternal()}
        label="resources.bo_common.b2b.employees.documents.fields.created_at"
        data-testid="BoExternalB2bEmployeeDocuments_CreatedAtField"
      />
    ),
    updated_at: (
      <UTCDateField
        showTime={!isBoExternal()}
        label="resources.bo_common.b2b.employees.documents.fields.updated_at"
        data-testid="BoExternalB2bEmployeeDocuments_UpdatedAtField"
      />
    ),
  }) satisfies ColumnList<B2bEmployeeDocumentsListItem>;

export const B2bEmployeeDocumentsList = () => {
  const resourceBasePath = useResourceBasePath();
  const employee =
    useRecordContext<GetBoExternalEmployeeDocumentDetailsEndpoint['Client']['ResponseBody']>();

  if (G.isUndefined(employee)) {
    return null;
  }

  return (
    <ReferenceManyField
      target="employee_id"
      reference={`${resourceBasePath}employees/documents`}
      label={false}
      filter={{
        employee_id: employee.id,
      }}
      sort={{
        field: 'id',
        order: 'DESC',
      }}
      perPage={10}
      pagination={<Pagination />}
      data-testid="BoExternalB2bEmployeeDocumentsList_DocumentsList">
      <DatagridTyped<B2bEmployeeDocumentsListItem>
        rowClick="show"
        sortableColumns={getB2bEmployeeDocumentsSortColumns}
        columnRegistry={b2bEmployeesDocumentsListColumns(resourceBasePath)}
        displayedColumns={[
          'id',
          'document_url',
          'document_type',
          'source',
          'is_archived',
          'created_at',
          'updated_at',
        ]}
        bulkActionButtons={false}
        empty={
          <EmptyScreen
            label="resources.bo_common.b2b.employees.documents.empty"
            svg={<EmptyFolderSvg />}
          />
        }
        showCard={true}
      />
    </ReferenceManyField>
  );
};

export const B2bEmployeeDocumentShow: FC = () => {
  const resource = useResourceContext();
  const resourceBasePath = useResourceBasePath();

  const navigateTo = (record: RaRecord<Identifier>) =>
    `/${resource.replace('documents', String(record.employee_id))}/show?tab=documents`;

  return (
    <ShowWithNavigation actions={false} navigateTo={navigateTo}>
      <ShowSection
        label="general.sections.details"
        resource={`${resourceBasePath}employees/documents`}>
        <ShowFields<B2bEmployeeDocumentDetails>
          labelPrefix={'resources.bo_common.b2b.employees.documents.fields'}
          fields={b2bEmployeesDocumentsListColumns(resourceBasePath)}
        />
      </ShowSection>
    </ShowWithNavigation>
  );
};
